/**
 * The product described at four lengths, because four surfaces need four
 * lengths. Every surface takes one of these — none of them retypes its own.
 *
 * - `tagline`     — one sentence, used for page meta descriptions and og:description.
 * - `shortStore`  — Microsoft Store short description (1,000 char field; keep near 200:
 *                   it is what shows in Store search results).
 * - `longStore`   — Microsoft Store description (10,000 char field), written as
 *                   sections so it can be rendered as plain text for Partner
 *                   Center and as markup on the website.
 */
import { pricing, urls } from './identity.js';
/** One sentence. The website's default meta description and JSON-LD description. */
export const tagline = 'Transport software for Indian fleet owners: trips, bilty (LR), GST invoices, driver and party hesab, diesel cards, FASTag and Tally export. Works offline on Windows, macOS, Android and the web.';
/** Microsoft Store short description. Shown in search results. */
export const shortStore = 'Fleet and transport management for logistics businesses — trips, trucks, drivers, parties, fuel, GPS, job cards, settlements, and accounting in one offline-first desktop workspace, with the same workspace in the browser.';
/** Microsoft Store long description, as structured sections. */
export const longStore = [
    {
        paragraphs: [
            'Transport Book is the desktop workspace for transport operators, fleet owners, and logistics teams. It puts day-to-day dispatch and the day-to-day books in one application — and it keeps working when your internet does not.',
            "Your records are stored on your own computer first and synchronised with your organisation's workspace through the Transport Book server whenever a connection is available. A dropped line slows nothing down: the office keeps booking trips, raising bills, and closing job cards, and everything catches up on its own.",
        ],
    },
    {
        heading: 'Trips and dispatch',
        bullets: [
            'Plan and track trips and sub-trips end to end, from booking through loading, delivery, and POD collection, with the margin visible on each trip.',
            'See what is pending — POD not received, loading slip not raised, trip not closed — instead of hunting for it.',
            'Match the right driver to the next load using availability and performance, not memory.',
            'Every truck keeps a driver history: who drove, between which dates and readings, and why the stint ended.',
            'Keep parties, trucks, drivers, firms, and companies in one master record set that every screen reads from.',
            'Open any record by its id, and search across the whole workspace from one box.',
            'Rate cards hold the agreed fare — per party or vehicle owner, lane and vehicle type — and fill the trip’s fare on entry.',
        ],
    },
    {
        heading: 'Maintenance and inventory',
        bullets: [
            'Raise job cards for repairs and servicing, with parts, labour, and cost captured against the vehicle.',
            'Track tyres individually through their whole life — fitted, removed, retreaded, scrapped — separately from consumable stock.',
            'Run a real store: issue and receive parts, value stock at FIFO cost, and see what a vehicle has actually consumed.',
        ],
    },
    {
        heading: 'Money and accounting',
        bullets: [
            'Raise bills, record receipts, and settle them against the right trips, with part payments and on-account credit handled properly.',
            'Settle a period with a party in one basket: collect its trips and payments, agree the deductions, and close it with every payment applied oldest trip first.',
            'A Billing section with invoices, receipts, payment vouchers, GST filing, e-invoice and e-way bill portal tools, and a TDS register checked against your Form 26AS download; an issued invoice can be reprinted or reissued with a reason.',
            'Party ledgers with outstanding and ageing, exportable to PDF, Excel, CSV, or HTML, shareable as a password-protected link that expires when you say — or sent to the party by email or WhatsApp.',
            'A collections view that ranks customers by what is worth chasing today and sends single or bulk payment reminders by email or WhatsApp.',
            'Vehicle finance in one place: purchase, loan and instalment schedule, typed running costs spread across the period they cover, insurance claims, depreciation, and sale. Match bank debits to EMIs and see each marked paid, part paid, or overdue.',
            'Import bank statements and reconcile them: matching learns from the decisions you make, and suggests fuel and FASTag recharges, loan EMIs and vehicle-cost payments for the rows it recognises.',
            'Fuel cards from BPCL and Reliance: balances, card limits, and every transaction, with exceptions surfaced. Pump credit bills import from the PDF.',
            'Toll transactions recorded alongside the trips they belong to, with a toll history for every vehicle.',
            'Income and expense records book under their own head in the P&L and Tally, and capital accounts keep partners’ money in its own balance sheet section.',
            'Export to Tally when it is time to hand over to your accountant.',
        ],
    },
    {
        heading: 'Reports',
        bullets: [
            'Trip, expense, vehicle, outstanding, loading, salary, loan, and tax reports, ready to export with presets and a live preview.',
            'GST and TDS figures pulled from the entries you already made.',
            'Data-quality checks that catch the gaps before your accountant does.',
            'Intelligence screens for cost per km, lane profitability, leakage, fleet utilisation, and cash forecast — computed from the entries you already made.',
        ],
    },
    {
        heading: 'Fleet visibility',
        bullets: [
            'Live GPS positions and active trips on a single map that stays readable for large fleets, from your tracking provider or the driver’s phone — switched per truck, with no extra hardware.',
            'A fleet wall for the office TV: the moving fleet cycling on a big screen, each truck with its planned road and any detour drawn.',
            'Trips that move with the truck: past the origin a trip turns in-transit, at the destination unloading — delivery is always yours to confirm.',
            'Pin the route on a trip — paste a Google Maps link or type the stops — and the driver’s phone navigates it stop by stop.',
            'Renewal and service-due reminders for insurance, PUC, permits, e-way bills, and preventive service — with a one-step renewal that records the payment, moves the expiry forward, and keeps the new certificate.',
            'Distance and average-km checks against what was actually run.',
        ],
    },
    {
        heading: 'Documents',
        bullets: [
            'Attach PODs, bills, and paperwork to the record they belong to.',
            'Sync them to Google Drive, OneDrive, or Amazon S3 — or, with no cloud storage at all, your organisation’s computers share them with each other directly.',
            'A Health & repair screen checks every attached file and fixes what it finds — even recovering a file from another of your computers.',
        ],
    },
    {
        heading: 'Built for a team',
        bullets: [
            'Multiple users, each seeing the records their role allows.',
            'Windows and macOS on the desktop, the same workspace in any modern browser, and an Android companion app for the field.',
            'Transport Book Driver, a phone app for your drivers: their assigned trip one step at a time, photos from the road, expenses and advances sent for office approval, and live location while a trip is on.',
            'Every device works from its own copy and reconciles with the others; a Connected Devices screen shows who last synced and when.',
        ],
    },
    {
        heading: 'Desktop or browser',
        paragraphs: [
            'The desktop app keeps a full copy of your records on the machine, so it works offline, opens attached documents from your own storage, and updates itself. The web app at my.transportbook.app is the same workspace with nothing to install: sign in from any modern browser and work on the live records your organisation keeps on the Transport Book server. It needs a connection while you work, prints PDFs through the browser, and opens attached documents from your connected storage or an online office computer. Use the desktop where work must continue without a network; use the browser everywhere else.',
        ],
    },
    {
        paragraphs: [
            `Transport Book requires an organisation account. Start a free ${pricing.freeTrialDays}-day trial at ${urls.startTrial} — it opens straight away, with no approval — or request a demo at ${urls.requestDemo} to see it against your own operation.`,
        ],
    },
];
/**
 * Renders `longStore` as the plain text Partner Center expects: no markdown, no
 * HTML, headings upper-cased, bullets as `•`.
 */
export function renderStoreDescription(sections = longStore) {
    return sections
        .map(section => {
        const blocks = [];
        if (section.heading)
            blocks.push(section.heading.toUpperCase());
        if (section.paragraphs)
            blocks.push(...section.paragraphs);
        if (section.bullets)
            blocks.push(section.bullets.map(line => `• ${line}`).join('\n'));
        return blocks.join('\n');
    })
        .join('\n\n');
}
/** Onboarding support, shared by the website and public FAQ. */
export const onboardingMigration = {
    title: 'Bring your data. We’ll help you make the move.',
    description: 'Moving from another platform? We’ll help migrate your existing data to Transport Book as part of onboarding. Tell us what you use today, and we’ll work through the move with you.',
};
//# sourceMappingURL=descriptions.js.map